import { showPrice, VariantAttribute } from "@swo/shared";
import { calculateVariantsMatchingFilterSet, Multiselect } from "./computed-state";
import { Option } from "fp-ts/Option";
import { pipe } from "fp-ts/lib/function";
import { option } from "fp-ts";
import { PDPPickerState } from "./utils";
import { AttributeInteractionAction, attributeToPicker } from "./attributeToPicker";
import { PDPAction, PDPSwarovskiProductFrontend, PDPSwarovskiProductVariantFrontend } from "../state/types";
import { type TypedMoney } from "@swo/storefront";
import { PDPState } from "../availability/use-product-variant-state";
import { attributeEq } from "../state/attributes";
import React from "react";

export const getMultiselect =
  (
    selectedProduct: PDPSwarovskiProductFrontend,
    attributesSelection: PDPState["attributesSelection"],
    linkForClickedAttribute: PDPState["linkForClickedAttribute"],
    multiselects: NonNullable<PDPState["computed"]["multiselects"]>,
    dispatch: React.Dispatch<PDPAction>,
    showPrice: (price: TypedMoney) => string,
  ) =>
  (multiselect: Multiselect, stepIdx: number) => {
    const productVariants = selectedProduct.variants;
    const multiselectsLength = multiselects.length;

    function getPriceLabel(
      isAtLastStep: boolean,
      maybeIsExactMatchVariant: Option<PDPSwarovskiProductVariantFrontend>,
    ) {
      return pipe(
        isAtLastStep ? maybeIsExactMatchVariant : option.none,
        option.chainNullableK(v => v.price),
        option.map(price => showPrice(price)),
        option.toUndefined,
      );
    }

    const stepSelected = attributesSelection[stepIdx];
    const filterSet = attributesSelection.slice(0, stepIdx);

    function getStateWithAdditionalAttribute(attribute: VariantAttribute) {
      const filterSetWithAttribute = [...filterSet, attribute];

      const variants = calculateVariantsMatchingFilterSet(productVariants, filterSetWithAttribute);

      const isDisabled = multiselect.disabled || variants.length === 0;
      const isSelected = stepSelected ? attributeEq.equals(stepSelected, attribute) : false;

      const state: PDPPickerState = isDisabled ? "disabled" : isSelected ? "selected" : "selectable";

      const link = linkForClickedAttribute(attribute);

      const onClick: AttributeInteractionAction = isDisabled
        ? {
            type: "disabled",
          }
        : link
          ? {
              type: "attribute-clicked-link",
              value: attribute,
              link: link,
            }
          : {
              type: "attribute-clicked-button",
              value: attribute,
              action: () =>
                dispatch({
                  type: "attribute-clicked-button",
                  value: attribute,
                }),
            };

      const isAtLastStep = stepIdx === multiselectsLength - 1;
      const maybeIsExactMatchVariant = variants.length === 1 ? option.some(variants[0]!) : option.none;

      return {
        isAtLastStep,
        maybeIsExactMatchVariant,
        state,
        onClick,
      };
    }

    const extendedAttributesForPicker = multiselect.attributes.map(attribute => {
      const { isAtLastStep, maybeIsExactMatchVariant, state, onClick } = getStateWithAdditionalAttribute(attribute);
      const priceLabel = getPriceLabel(isAtLastStep, maybeIsExactMatchVariant);

      return attributeToPicker(attribute, onClick, state, priceLabel);
    });

    return {
      extendedAttributesForPicker,
      headline: multiselect.headline,
      disabled: multiselect.disabled,
      reactKey: multiselect.attributes.map(attr => JSON.stringify(attr.value)).join("@"),
    };
  };
